import _ from 'lodash'

import { PostFormat } from 'sharedKernel/api'
import { regexPatterns } from 'sharedKernel/regex'
import { IRegisterFormValues } from '../store'
import { v4 as uuidv4 } from 'uuid'

interface IRegisterData {
  id?: string
  name: string
  contactName: string
  contact: {
    mobile: string
  }
  email: string
  cnpj: string
  website: string
  password: string
  confirmPassword: string
  address: any
  country: string
  isUserTermsAccepted: boolean
  isEcosystemTermsAccepted: boolean
  evaluatorType?: string
  evaluatorRole?: string
  evaluatorInstitution?: string
  evaluatorDepartment?: string
  evaluatorEducation?: string
  metWeb?: string
  isSimplifiedResolver: boolean
  profileImage?: {
    mimetype: string
    name: string
    size: number
    companyId?: string
    path?: string
    extension?: string
    type?: string
    content?: string
  } | null
  startupInfo?: {
    activity: string
    target: string
    businessModel: string
    numberEmployees: string
    technologies: string
    developmentStage: number | null
    annualTurnover: number
    hasRaisedInvestment: boolean
    amount: number
    investmentSource: string
    pitch: string
    logo?: {
      mimetype: string
      name: string
      size: number
      companyId?: string
      path?: string
      extension?: string
      type?: string
      content?: string
    } | null
  }
}

export const createRegisterRequest = (form: IRegisterFormValues, url: string, acceptUserTerms: boolean, acceptEcosystemTerms: boolean): PostFormat => {
  const request: PostFormat = {
    url,
    data: {}
  }

  request.data = transformFormDataToPostObject(form, acceptUserTerms, acceptEcosystemTerms)
  return request
}

const transformFormDataToPostObject = (form: IRegisterFormValues, acceptUserTerms: boolean, acceptEcosystemTerms: boolean): any => {
  const cleanObject = Object.fromEntries(
    Object.entries(form).map(([key, value]) => [key, value.value])
  )

  const data: IRegisterData = {
    id: !cleanObject.id || cleanObject.id.trim() === ''
    ? '00000000-0000-0000-0000-000000000000'
    : cleanObject.id,
    name: cleanObject.startupName,
    contactName: cleanObject.contactName,
    contact: {
      mobile: cleanObject.phone.replace(regexPatterns.clearMaskedNumbers, '')
    },
    email: cleanObject.email,
    cnpj: cleanCNPJ(cleanObject.cnpj),
    website: cleanObject.startupWebsite,
    password: cleanObject.password,
    confirmPassword: cleanObject.confirmPassword,
    address: getAddress(cleanObject),
    country: cleanObject.country,
    isUserTermsAccepted: acceptUserTerms,
    isEcosystemTermsAccepted: acceptEcosystemTerms,
    evaluatorType: cleanObject.evaluatorType,
    evaluatorRole: cleanObject.evaluatorRole,
    evaluatorInstitution: cleanObject.evaluatorInstitution,
    evaluatorDepartment: cleanObject.evaluatorDepartment,
    evaluatorEducation: cleanObject.evaluatorEducation,
    isSimplifiedResolver: false,
    profileImage: cleanObject.startupLogo?.name
      ? {
          mimetype: cleanObject.startupLogo.mimetype || '',
          name: cleanObject.startupLogo.name
            ? btoa(unescape(encodeURIComponent(cleanObject.startupLogo.name)))
            : '',
          size: cleanObject.startupLogo.size || 0,
          companyId: cleanObject.startupLogo.companyId || undefined,
          path: cleanObject.startupLogo.path || '',
          extension: cleanObject.startupLogo.extension || '',
          type: cleanObject.startupLogo.type || 'startupLogo',
          content: cleanObject.startupLogo.content || ''
        }
      : null
  }

  if (!cleanObject.evaluatorType) {
    data.metWeb = cleanObject.metWeb
    data.isSimplifiedResolver = cleanObject.metWeb ? true : false
    data.isEcosystemTermsAccepted = data.isSimplifiedResolver ? true : data.isEcosystemTermsAccepted
    data.startupInfo = {
      activity: cleanObject.activity,
      target: cleanObject.target,
      businessModel: cleanObject.businessModel,
      numberEmployees: cleanObject.numberEmployees,
      technologies: cleanObject.technologies,
      developmentStage: cleanObject.developmentStage?.trim() ? transformIntNumber(cleanObject.developmentStage) : null,
      annualTurnover: transformFloatNumber(cleanObject.annualTurnover),
      hasRaisedInvestment: cleanObject.hasRaisedInvestment === true,
      amount: transformFloatNumber(cleanObject.amount),
      investmentSource: cleanObject.otherInvestmentSource?.trim() ? cleanObject.otherInvestmentSource : cleanObject.investmentSource,
      pitch: cleanObject.pitch,
      logo: cleanObject.startupLogo?.name
        ? {
            mimetype: cleanObject.startupLogo.mimetype || '',
            name: cleanObject.startupLogo.name
              ? btoa(unescape(encodeURIComponent(cleanObject.startupLogo.name)))
              : '',
            size: cleanObject.startupLogo.size || 0,
            companyId: cleanObject.startupLogo.companyId || undefined,
            path: cleanObject.startupLogo.path || '',
            extension: cleanObject.startupLogo.extension || '',
            type: cleanObject.startupLogo.type || 'startupLogo',
            content: cleanObject.startupLogo.content || ''
          }
        : null
    }
  }

  return { id: `${String(uuidv4())}`, data: data }
}

const transformFloatNumber = (value: string): number => {
  return value ? parseFloat(value) : 0.0
}

const transformIntNumber = (value: string): number => {
  return value ? parseInt(value) : 0
}

const getAddress = (cleanObject: any): any => {
  cleanObject.zipcode = cleanObject.zipcode.replace(regexPatterns.clearMaskedNumbers, '')

  return {
    name: '',
    ..._.pick(cleanObject, ['zipcode', 'lineOne', 'number', 'lineTwo', 'neighborhood', 'city', 'state'])
  }
}

const cleanCNPJ = (cnpj: string): string => {
  if (cnpj == null) {
    return ''
  }
  return cnpj.replace(/[^\d]/g, '')
}
